import React from 'react';
import { Car, Users, Luggage, Gauge, Snowflake, MapPin, Check } from 'lucide-react';
import { StorytellingLayout, ImageCarousel, ExpandableCard } from '../base';

/**
 * Tela de storytelling para aluguel de carros
 * 
 * Single Source of Truth: Backend envia todos os dados calculados.
 * Frontend apenas renderiza.
 * 
 * Schema esperado (details):
 * - company, category, model
 * - pickup_location, pickup_date, pickup_time
 * - dropoff_location, dropoff_date, dropoff_time
 * - days (calculado pelo backend)
 */
export default function StorytellingCar({
  block,
  stepIndicator,
  narrativeText,
  ctaText,
  onContinue,
  onBack,
  onShare
}) {
  const details = block.details || {};

  // Monta array de imagens
  const buildImages = () => {
    const imgs = [];
    if (block.image_url) imgs.push(block.image_url);
    if (details.image_url_2) imgs.push(details.image_url_2);
    if (details.image_url_3) imgs.push(details.image_url_3);

    // Fallback para imagens default
    if (imgs.length === 0) {
      return [
        'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800&q=80',
        'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800&q=80'
      ];
    }
    return imgs;
  };
  const images = buildImages();

  // Formata data
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Formata período
  const formatDateRange = () => {
    const pickup = details.pickup_date || block.date;
    const dropoff = details.dropoff_date;
    if (!pickup || !dropoff) return '';

    const pickupDate = new Date(pickup + 'T00:00:00');
    const dropoffDate = new Date(dropoff + 'T00:00:00');

    const formatDay = (d) => d.getDate().toString().padStart(2, '0');
    const months = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

    return `${formatDay(pickupDate)}-${formatDay(dropoffDate)} ${months[dropoffDate.getMonth()]}`;
  };

  // Usa days do backend (Single Source of Truth)
  const days = details.days || 1;

  // Ícone de feature
  const FeatureBadge = ({ icon: Icon, text }) => (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0.75rem',
      backgroundColor: 'rgba(93, 212, 195, 0.15)',
      borderRadius: '0.5rem'
    }}>
      <Icon size={14} color="#5DD4C3" strokeWidth={2} />
      <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#5DD4C3' }}>
        {text}
      </span>
    </div>
  );

  const cardContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Modelo e categoria */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{
          width: '48px',
          height: '48px',
          backgroundColor: 'rgba(93, 212, 195, 0.15)',
          borderRadius: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <Car size={24} color="#5DD4C3" strokeWidth={2} />
        </div>
        <div>
          <p style={{
            fontSize: '1.0625rem',
            fontWeight: 600,
            color: 'white',
            margin: 0,
            lineHeight: 1.3
          }}>
            {block.title || details.model || 'Veículo'}
          </p>
          {details.category && (
            <p style={{
              fontSize: '0.8125rem',
              color: 'rgba(255, 255, 255, 0.6)',
              margin: '0.25rem 0 0 0'
            }}>
              {details.similar ? `(ou similar) ${details.category}` : details.category}
            </p>
          )}
        </div>
      </div>

      {/* Especificações principais */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Passageiros */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{
            width: '44px',
            height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Users size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.passengers || details.seats || 5}
          </p>
          <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Passageiros
          </p>
        </div>

        {/* Malas */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{
            width: '44px',
            height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Luggage size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.luggage || details.bags || 2}
          </p>
          <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Malas
          </p>
        </div>

        {/* Câmbio */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
          <div style={{
            width: '44px',
            height: '44px',
            backgroundColor: 'rgba(93, 212, 195, 0.15)',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Gauge size={20} color="#5DD4C3" strokeWidth={2} />
          </div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.transmission === 'automatic' || details.transmission === 'Auto' ? 'Auto' : 'Manual'}
          </p>
          <p style={{ fontSize: '0.6875rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Câmbio
          </p>
        </div>
      </div>

      {/* Features */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {details.air_conditioning !== false && (
          <FeatureBadge icon={Snowflake} text="Ar condicionado" />
        )}
        {details.unlimited_mileage && (
          <FeatureBadge icon={Check} text="Km ilimitada" />
        )}
        {details.free_cancellation && (
          <FeatureBadge icon={Check} text="Cancelamento grátis" />
        )}
      </div>

      {/* Local de retirada */}
      {(details.pickup_location || details.location) && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.5rem',
          paddingTop: '0.75rem',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <MapPin size={16} color="rgba(255, 255, 255, 0.6)" strokeWidth={2} style={{ marginTop: '0.125rem' }} />
          <div>
            <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
              Retirada e devolução
            </p>
            <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0.25rem 0 0 0' }}>
              {details.pickup_location || details.location}
            </p>
          </div>
        </div>
      )}

      {/* Período */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Retirada
          </p>
          <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: '0.25rem 0 0 0' }}>
            {formatDate(block.date || details.pickup_date)}
          </p>
          {details.pickup_time && (
            <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
              {details.pickup_time}
            </p>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
            Devolução
          </p>
          <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: '0.25rem 0 0 0' }}>
            {formatDate(details.dropoff_date)}
          </p>
          {details.dropoff_time && (
            <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
              {details.dropoff_time}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const expandedContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {/* O que está incluído */}
      {details.included && details.included.length > 0 && (
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.25rem 0' }}>
            O que está incluído
          </p>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, margin: 0 }}>
            {details.included.map((item, idx) => (
              <span key={idx}>• {item}<br /></span>
            ))}
          </p>
        </div>
      )}

      {/* Informações importantes */}
      {details.requirements && details.requirements.length > 0 && (
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: '0 0 0.25rem 0' }}>
            Informações importantes
          </p>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, margin: 0 }}>
            {details.requirements.map((item, idx) => (
              <span key={idx}>• {item}<br /></span>
            ))}
          </p>
        </div>
      )}

      {/* Locadora */}
      {details.rental_company && (
        <div>
          <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>Locadora</p>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.rental_company}
          </p>
        </div>
      )}

      {/* Política de combustível */}
      {details.fuel_policy && (
        <div>
          <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>Combustível</p>
          <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
            {details.fuel_policy}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <StorytellingLayout
      backgroundImage={images[0]}
      title="Aluguel de Carro"
      narrativeText={narrativeText}
      stepIndicator={stepIndicator}
      ctaText={ctaText}
      onContinue={onContinue}
      onBack={onBack}
      onShare={onShare}
    >
      <ImageCarousel images={images} />
      <ExpandableCard expandedContent={expandedContent}>
        {cardContent}
      </ExpandableCard>
    </StorytellingLayout>
  );
}